import React from 'react';
import { Box, CircularProgress, LinearProgress, Stack, Typography, Paper, Container } from '@mui/material';
import { useDatastore } from '../../hooks';
import SyncIcon from '@mui/icons-material/Sync';
import { ReactComponent as Logo } from '../../assets/images/mrllogo.svg';

interface SyncBlockerProps {
  children: React.ReactNode;
}

const SyncBlocker: React.FC<SyncBlockerProps> = ({ children }) => {
  const { isSyncing, isDatastoreReady, isEssentialReady, syncProgress } = useDatastore();

  // Phased load is always on: block only until the essential master-data is ready,
  // then release the app and let heavy models (SSCC / TripSheet) sync in the
  // background. The header sync popover auto-opens so users can watch progress.
  const shouldBlock = !isEssentialReady;

  const syncedModels = Object.keys(syncProgress);
  const totalSynced = syncedModels.reduce((total, model) => {
    const counts = syncProgress[model];
    return total + counts.new + counts.updated + counts.deleted;
  }, 0);
  const progressMessage =
    totalSynced > 0
      ? `${totalSynced} changes synced across ${syncedModels.length} data types`
      : isSyncing
        ? 'Syncing data from the server...'
        : 'Preparing to sync data...';

  if (!shouldBlock) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, #000000 0%, #424242 100%)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
      <Container maxWidth="sm">
        <Paper
          elevation={12}
          sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: 4,
            backgroundColor: 'background.paper',
            border: '1px solid rgba(17, 78, 199, 0.1)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
          <Stack spacing={3} alignItems="center">
            {/* Logo */}
            <Box sx={{ mb: 1 }}>
              <Logo width="120" height="60" />
            </Box>

            {/* Icon and spinner */}
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                size={70}
                thickness={3.5}
                sx={{
                  color: '#114EC7',
                  animationDuration: '1.8s'
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                <SyncIcon sx={{ fontSize: 28, color: '#114EC7' }} />
              </Box>
            </Box>

            {/* Main message */}
            <Stack spacing={1.5} alignItems="center">
              <Typography
                variant="h4"
                fontWeight="600"
                sx={{
                  color: '#114EC7',
                  fontFamily: 'Avenir, "Century Gothic", sans-serif'
                }}>
                {!isDatastoreReady && isSyncing ? 'Syncing FTL Data' : 'Loading FTL Data'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#595959',
                  fontFamily: 'Avenir, "Century Gothic", sans-serif',
                  maxWidth: '400px',
                  lineHeight: 1.6
                }}></Typography>
            </Stack>

            {/* Progress information */}
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 2.5,
                  color: '#595959',
                  fontFamily: 'Avenir, "Century Gothic", sans-serif',
                  fontWeight: totalSynced > 0 ? 500 : 400
                }}>
                {progressMessage}
              </Typography>

              {!isDatastoreReady && (
                <LinearProgress
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(17, 78, 199, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      background: 'linear-gradient(90deg, #114EC7 0%, #5C94FF 100%)'
                    }
                  }}
                />
              )}
            </Box>

            {/* Additional info */}
            <Typography
              variant="caption"
              sx={{
                color: '#595959',
                fontFamily: 'Avenir, "Century Gothic", sans-serif',
                fontStyle: 'italic',
                fontSize: '0.875rem'
              }}>
              The application will be available once essential data has loaded
            </Typography>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default SyncBlocker;
